import React, { useState, useEffect, useCallback, useRef } from 'react';
import { socketService } from '../services/socketService';
import { OnlineRoomState, OnlinePlayer, GameMode, YatzyScores } from '../types/game';
import ScoreCard from './ScoreCard';
import Die2D from './Die2D';
import DiceBoard3D from './DiceBoard3D';
import './OnlineGameMode.css';
import './score-details.css';
import styled from 'styled-components';

interface OnlineGameModeProps {
  onExitToMenu: () => void;
}

type OnlineGamePhase = 'menu' | 'creating' | 'joining' | 'lobby' | 'playing';

interface ConnectionState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
}

const OnlineGameMode: React.FC<OnlineGameModeProps> = ({ onExitToMenu }) => {

  const emptyScores: YatzyScores = {
    ones: null,
    twos: null,
    threes: null,
    fours: null,
    fives: null,
    sixes: null,
    onePair: null,
    twoPairs: null,
    threeOfAKind: null,
    fourOfAKind: null,
    smallStraight: null,
    largeStraight: null,
    fullHouse: null,
    chance: null,
    yatzy: null
  };

  const [phase, setPhase] = useState<OnlineGamePhase>('menu');
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    isConnected: false,
    isConnecting: false,
    error: null
  });
  const [roomState, setRoomState] = useState<OnlineRoomState | null>(null);
  const roomStateRef = useRef(roomState);
  const [isRolling, setIsRolling] = useState<boolean>(false);
  useEffect(() => {
    roomStateRef.current = roomState;
  }, [roomState]);

  const [playerId, setPlayerId] = useState<string | null>(socketService.getPlayerId());
  const [playerName, setPlayerName] = useState<string>('');
  const [roomIdInput, setRoomIdInput] = useState<string>('');
  const [selectedGameMode, setSelectedGameMode] = useState<GameMode>('classic');
  const [eventLog, setEventLog] = useState<Array<{ id: string; message: string; timestamp: string }>>([]);

  const handleRoomUpdate = useCallback((roomData: any) => {
    const newRoomState = socketService.mapRoom(roomData);
    
    // Kontrollera om vi bytt spelare
    if (newRoomState.currentPlayerIndex !== roomStateRef.current?.currentPlayerIndex) {
      // Ny spelare - återställ tärningsanimationen
      setIsRolling(false);
      
      // Säkerställ att tärningskastet återställs (3 kast kvar)
      if (newRoomState.rollsLeft !== 3) {
        console.log('Resetting dice for new player turn - should have 3 rolls');
      }
    }
    
    setRoomState(newRoomState);
    if (newRoomState.gameState === 'playing') {
      setPhase('playing');
    } else {
      setPhase('lobby');
    }
  }, []);

  const setupSocketListeners = useCallback(() => {
    socketService.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setConnectionState(prev => ({ ...prev, isConnected: false }));
      setRoomState(null);
      setPlayerId(null);
      setPhase('menu');
    });

    socketService.on('room_created', ({ room, playerId: newPlayerId }) => {
      socketService.setPlayerId(newPlayerId);
      setPlayerId(newPlayerId);
      handleRoomUpdate(room);
    });

    socketService.on('room_joined', ({ room, playerId: newPlayerId }) => {
      socketService.setPlayerId(newPlayerId);
      setPlayerId(newPlayerId);
      handleRoomUpdate(room);
    });

    socketService.on('room_updated', (room) => {
      handleRoomUpdate(room);
    });

    socketService.on('game_event', (event) => {
      if (event && typeof event === 'object') {
        setEventLog(prevLog => [
          {
            id: `${event.type}-${event.timestamp}`,
            message: formatGameEvent(event, roomStateRef.current?.players ?? []),
            timestamp: event.timestamp as string ?? new Date().toISOString(),
          },
          ...prevLog
        ].slice(0, 25));

        if (event.type === 'game_finished') {
          setPhase('lobby');
        }
      }
    });

    socketService.on('error', (error) => {
      console.error('Server error:', error);
      setConnectionState(prev => ({ ...prev, error: error.message }));
      setPhase('menu');
      setRoomState(null);
    });
  }, [handleRoomUpdate]);

  useEffect(() => {
    (async () => {
      setConnectionState(prev => ({ ...prev, isConnecting: true, error: null }));
      try {
        await socketService.connect();
        setConnectionState({ isConnected: true, isConnecting: false, error: null });
        setupSocketListeners();
      } catch (error) {
        console.error('Failed to connect to server:', error);
        setConnectionState({ isConnected: false, isConnecting: false, error: 'Kunde inte ansluta till servern. Kontrollera din internetanslutning.' });
      }
    })();

    return () => {
      socketService.disconnect();
    };
  }, [setupSocketListeners]);

  const handleCreateRoom = () => {
    if (!playerName.trim()) {
      setConnectionState(prev => ({ ...prev, error: 'Ange ditt namn först' }));
      return;
    }

    setPhase('creating');
    setConnectionState(prev => ({ ...prev, error: null }));

    try {
      // Add mode-specific settings when creating a room
      const modeSettings: any = {};
      
      if (selectedGameMode === 'time-challenge') {
        modeSettings.turnTimeLimit = 30; // 30 seconds per turn
      } else if (selectedGameMode === 'tournament') {
        modeSettings.seriesLength = 3; // Best of 3
      }
      
      socketService.createRoom(playerName.trim(), selectedGameMode, false, modeSettings);
    } catch (error) {
      console.error('Failed to create room:', error);
      setConnectionState(prev => ({ ...prev, error: 'Kunde inte skapa rum' }));
      setPhase('menu');
    }
  };

  const handleJoinRoom = () => {
    if (!playerName.trim()) {
      setConnectionState(prev => ({ ...prev, error: 'Ange ditt namn först' }));
      return;
    }
    if (!roomIdInput.trim()) {
      setConnectionState(prev => ({ ...prev, error: 'Ange rum-ID' }));
      return;
    }

    setPhase('joining');
    setConnectionState(prev => ({ ...prev, error: null }));

    try {
      socketService.joinRoom(roomIdInput.trim(), playerName.trim());
    } catch (error) {
      console.error('Failed to join room:', error);
      setConnectionState(prev => ({ ...prev, error: 'Kunde inte gå med i rummet' }));
      setPhase('menu');
    }
  };

  const handleLeaveRoom = () => {
    socketService.leaveRoom();
    setRoomState(null);
    setPlayerId(null);
    setPhase('menu');
  };

  const handleStartGame = () => {
    socketService.startGame();
  };

  const handleToggleReady = () => {
    try {
      socketService.toggleReady();
      setConnectionState(prev => ({ ...prev, error: null }));
    } catch (error) {
      console.error('Failed to toggle ready state', error);
      setConnectionState(prev => ({ ...prev, error: 'Kunde inte uppdatera redo-status.' }));
    }
  };

  const renderConnectionStatus = () => {
    if (connectionState.isConnecting) {
      return (
        <div className="online-status connecting">
          <div className="loading-spinner"></div>
          <span>Ansluter till server...</span>
        </div>
      );
    }

    if (!connectionState.isConnected) {
      return (
        <div className="online-status disconnected">
          <span>⚠️ Inte ansluten till server</span>
          {connectionState.error && (
            <div className="error-message">{connectionState.error}</div>
          )}
        </div>
      );
    }

    return (
      <div className="online-status connected">
        <span>✅ Ansluten till server</span>
      </div>
    );
  };

  const renderMainMenu = () => (
    <div className="online-menu">
      <h2>Online Multiplayer</h2>
      
      {renderConnectionStatus()}
      
      {connectionState.error && (
        <div className="error-message">{connectionState.error}</div>
      )}

      <div className="player-name-section">
        <label htmlFor="player-name">Ditt namn:</label>
        <input
          id="player-name"
          type="text"
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          placeholder="Ange ditt namn"
          maxLength={20}
          disabled={!connectionState.isConnected}
        />
      </div>

      <div className="game-mode-section">
        <label htmlFor="game-mode">Spelläge:</label>
        <select
          id="game-mode"
          value={selectedGameMode}
          onChange={(e) => setSelectedGameMode(e.target.value as GameMode)}
          disabled={!connectionState.isConnected}
        >
          <option value="classic">Klassisk Yatzy</option>
          <option value="time-challenge">Tidsutmaning (30s per drag)</option>
          <option value="tournament">Turnering (Bäst av 3)</option>
        </select>
      </div>

      <div className="online-actions">
        <button
          onClick={handleCreateRoom}
          disabled={!connectionState.isConnected || !playerName.trim()}
          className="create-room-btn"
        >
          Skapa nytt rum
        </button>

        <div className="join-room-section">
          <input
            type="text"
            value={roomIdInput}
            onChange={(e) => setRoomIdInput(e.target.value.toUpperCase())}
            placeholder="Rum-ID (t.ex. ABC123)"
            maxLength={6}
            disabled={!connectionState.isConnected}
          />
          <button
            onClick={handleJoinRoom}
            disabled={!connectionState.isConnected || !playerName.trim() || !roomIdInput.trim()}
            className="join-room-btn"
          >
            Gå med i rum
          </button>
        </div>
      </div>

      <button onClick={onExitToMenu} className="back-btn">
        Tillbaka till huvudmeny
      </button>
    </div>
  );

  const renderLobby = () => {
    if (!roomState) return null;

    const localPlayerId = playerId ?? socketService.getPlayerId();
    const isHost = !!localPlayerId && localPlayerId === roomState.host;
    const canStartGame =
      isHost &&
      roomState.players.length >= 2 &&
      roomState.players.every(p => p.isReady);
    const localPlayer = roomState.players.find(player => player.id === localPlayerId) ?? null;

    return (
      <div className="online-lobby">
        <h2>Rum: {roomState.code}</h2>
        
        <div className="room-info">
          <p>Spelläge: {roomState.gameMode}</p>
          <p>Spelare: {roomState.players.length}/{roomState.maxPlayers}</p>
        </div>

        <div className="players-list">
          <h3>Spelare i rummet:</h3>
          {roomState.players.map(player => (
            <div key={player.id} className={`player-item ${player.isHost ? 'host' : ''}`}>
              <span className="player-name">
                {player.name} 
                {player.isHost && ' 👑'}
              </span>
              <span className={`player-status ${player.isConnected ? 'connected' : 'disconnected'}`}>
                {player.isConnected ? '🟢 Online' : '🔴 Frånkopplad'}
              </span>
              <span className={`player-ready ${player.isReady ? 'ready' : 'not-ready'}`}>
                {player.isReady ? '✅ Redo' : '⌛ Väntar'}
              </span>
            </div>
          ))}
        </div>

        <div className="lobby-actions">
          {localPlayer && (
            <button
              type="button"
              onClick={handleToggleReady}
              className={`ready-toggle-btn ${localPlayer.isReady ? 'ready' : ''}`}
              disabled={!connectionState.isConnected}
            >
              {localPlayer.isReady ? 'Markera som ej redo' : 'Markera som redo'}
            </button>
          )}
          {isHost && (
            <button
              onClick={handleStartGame}
              disabled={!canStartGame}
              className="start-game-btn"
            >
              {canStartGame ? 'Starta spel' : `Väntar på spelare (${roomState.players.length}/2 minimum)`}
            </button>
          )}
          
          <button onClick={handleLeaveRoom} className="leave-room-btn">
            Lämna rum
          </button>
        </div>
      </div>
    );
  };

  const renderGame = () => {
    if (!roomState) return null;

    const localPlayerId = playerId ?? socketService.getPlayerId();
    const currentPlayer = roomState.players[roomState.currentPlayerIndex] ?? null;
    const isMyTurn = currentPlayer?.id === localPlayerId;
    const handleSelectCategory = (category: keyof YatzyScores) => {
      try {
        socketService.selectScore(category);
        setConnectionState(prev => ({ ...prev, error: null }));
        
        // Tournament mode specific logic
        if (roomState.gameMode === 'tournament') {
          // Check if this is the last category in the round
          const allCategories = Object.keys(roomState.players[0].scores);
          const filledCategories = allCategories.filter(cat => 
            roomState.players.every(p => p.scores[cat as keyof YatzyScores] !== null)
          );
          
          if (filledCategories.length === allCategories.length) {
            // End of a tournament round
            console.log('Tournament round complete');
            // The server should handle advancing to next round
          }
        }
      } catch (error) {
        console.error('Failed to select score', error);
        setConnectionState(prev => ({ ...prev, error: 'Kunde inte välja kategori.' }));
      }
    };

    const handleRollDice = () => {
      try {
        // Time-challenge mode specific logic
        if (roomState.gameMode === 'time-challenge') {
          // We don't need special handling here since the timer is UI-based
          // but we could add server-side time limit enforcement if needed
        }
        
        // Säkerställ att det är första kastet efter ny spelare
        if (roomState.rollsLeft === 3) {
          // När det är första kastet, se till att alla tärningar är återställda och olåsta
          try {
            socketService.resetPlayerDice();
          } catch (error) {
            console.error('Failed to reset dice', error);
          }
        }
        socketService.rollDice();
        setIsRolling(true);
        
        // Återställ animationstillståndet efter en kort fördröjning
        setTimeout(() => {
          setIsRolling(false);
        }, 800);
      } catch (error) {
        console.error('Failed to roll dice', error);
        setConnectionState(prev => ({ ...prev, error: 'Kunde inte kasta tärningarna.' }));
        setIsRolling(false);
      }
    };    const handleToggleLock = (dieId: number) => {
      // Förhindra låsning om det inte är din tur eller om inga kast har gjorts ännu
      if (!isMyTurn) return;
      if (roomState.rollsLeft === 3) {
        // Om inga kast har gjorts ännu (3 kast kvar), förhindra låsning
        setConnectionState(prev => ({ 
          ...prev, 
          error: 'Du måste kasta tärningarna först innan du kan låsa dem.' 
        }));
        return;
      }
      
      try {
        socketService.lockDie(dieId);
        setConnectionState(prev => ({ ...prev, error: null }));
      } catch (error) {
        console.error('Failed to toggle die lock', error);
        setConnectionState(prev => ({ ...prev, error: 'Kunde inte uppdatera tärningens status.' }));
      }
    };

    return (
      <div className="online-game unified-game-ui">
        <div className="game-header">
          <h2>Online Yatzy - Rum: {roomState.code}</h2>
          <div className="game-mode-info">
            {roomState.gameMode === 'time-challenge' && (
              <div className="timer-container">
                <TurnTimer
                  timeLimit={30}
                  isMyTurn={isMyTurn}
                  currentPlayer={currentPlayer?.name || ''}
                  onTimeUp={() => {
                    // Auto-select first available category with 0 points (strike)
                    if (isMyTurn) {
                      const scores = currentPlayer?.scores;
                      if (scores) {
                        const emptyCategory = (Object.keys(scores) as (keyof YatzyScores)[]).find(k => scores[k] === null);
                        if (emptyCategory) {
                          handleSelectCategory(emptyCategory);
                        }
                      }
                    }
                  }}
                />
              </div>
            )}
            {roomState.gameMode === 'tournament' && (
              <div className="tournament-info">
                <div>Turneringsläge: Bäst av 3</div>
                <div>Omgång: {roomState.currentRound} / 3</div>
              </div>
            )}
          </div>
          <div className={`turn-indicator ${isMyTurn ? 'my-turn' : 'opponent-turn'}`}>
            {isMyTurn ? (
              <>
                <div>🎮 DIN TUR!</div>
                {roomState.rollsLeft === 3 && (
                  <div className="turn-instruction">
                    Tryck på "Kasta tärningar" för att börja
                  </div>
                )}
              </>
            ) : (
              `⌛ Väntar på ${currentPlayer?.name}s drag...`
            )}
          </div>
        </div>

        <div className="players-scores">
          {roomState.players.map(player => (
            <div 
              key={player.id} 
              className={`player-score ${player.id === currentPlayer?.id ? 'active' : ''} ${player.id === localPlayerId ? 'is-local-player' : ''}`}
            >
              <div className="player-name-container">
                <h4>{player.name} {player.id === localPlayerId && '(Du)'}</h4>
                {player.id === currentPlayer?.id && (
                  <div className="active-player-indicator">
                    {player.id === localPlayerId ? '🎲 Din tur' : '⏳ Spelar nu'}
                  </div>
                )}
              </div>
              <div className="score-details">
                <div className="upper-section">Övre: {player.upperSectionScore || 0}</div>
                {(player.upperSectionBonus || 0) > 0 && <div className="bonus">Bonus: {player.upperSectionBonus}</div>}
                <div className="total-score">Totalt: {player.totalScore}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="game-content unified-board">
          <div role="group" aria-label="Aktuella tärningar" style={{ width: '100%' }}>
            {roomState.dice.length === 0 ? (
              <p className="dice-placeholder">Ingen tärningsdata ännu.</p>
            ) : (
              <DiceBoard3D
                dice={roomState.dice}
                isRolling={isRolling}
                canInteract={isMyTurn && roomState.rollsLeft < 3 && !isRolling}
                onToggleDie={handleToggleLock}
                overlay={
                  !isMyTurn ? (
                    <div className="opponent-turn-overlay">
                      <div className="overlay-message">Väntar på motspelaren...</div>
                    </div>
                  ) : null
                }
              />
            )}
            {false && (
              <Die2D index={0} value={1} isLocked={false} onClick={() => {}} />
            )}
          </div>

          <div className="roll-controls">
            <button
              type="button"
              className="roll-dice-btn"
              onClick={handleRollDice}
              disabled={!isMyTurn || roomState.rollsLeft <= 0}
            >
              🎲 Kasta tärningar
            </button>
            <div className="rolls-remaining" aria-live="polite">
              Kast kvar: {roomState.rollsLeft}
            </div>
            {connectionState.error && (
              <div className="game-error-message" aria-live="assertive">
                {connectionState.error}
              </div>
            )}
          </div>

          <ScoreCard
            scores={currentPlayer?.scores ?? emptyScores}
            dice={roomState.dice}
            onScoreSelect={handleSelectCategory}
            rollsLeft={roomState.rollsLeft}
            upperSectionScore={currentPlayer?.upperSectionScore ?? 0}
            upperSectionBonus={currentPlayer?.upperSectionBonus ?? 0}
            totalScore={currentPlayer?.totalScore ?? 0}
            highlightCategory={null}
          />

          <div
            aria-live="polite"
            className="turn-log"
            role="log"
          >
            <h4>Senaste händelser</h4>
            {eventLog.length === 0 ? (
              <p className="turn-placeholder">Inga händelser ännu.</p>
            ) : (
              <ul>
                {eventLog.map((entry, index) => (
                  <li key={`${entry.id}-${index}`}>
                    <span className="event-message">{entry.message}</span>
                    <span className="event-time">{formatTimestamp(entry.timestamp)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>

        <button onClick={handleLeaveRoom} className="leave-game-btn">
          Lämna spel
        </button>
      </div>
    );
  };

  const renderLoading = () => (
    <div className="online-loading">
      <div className="loading-spinner"></div>
      <p>
        {phase === 'creating' && 'Skapar rum...'}
        {phase === 'joining' && 'Går med i rum...'}
      </p>
    </div>
  );

  return (
    <div className="online-game-mode">
      {phase === 'menu' && renderMainMenu()}
      {(phase === 'creating' || phase === 'joining') && renderLoading()}
      {phase === 'lobby' && renderLobby()}
      {phase === 'playing' && renderGame()}
    </div>
  );
}

// Timer component for time-challenge mode
interface TurnTimerProps {
  timeLimit: number;
  isMyTurn: boolean;
  currentPlayer: string;
  onTimeUp?: () => void;
}

const TimerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
`;

const TimerDisplay = styled.div<{ $timeRemaining: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border-radius: 20px;
  font-weight: bold;
  background-color: ${props => {
    if (props.$timeRemaining <= 5) return 'rgba(255, 59, 48, 0.9)';
    if (props.$timeRemaining <= 10) return 'rgba(255, 149, 0, 0.9)';
    return 'rgba(52, 199, 89, 0.8)';
  }};
  color: white;
  transition: background-color 0.5s ease;
`;

const TurnTimer: React.FC<TurnTimerProps> = ({ timeLimit, isMyTurn, currentPlayer, onTimeUp }) => {
  const [timeRemaining, setTimeRemaining] = useState(timeLimit);
  const timeUpFired = React.useRef(false);
  
  useEffect(() => {
    // Reset timer when turn changes
    setTimeRemaining(timeLimit);
    timeUpFired.current = false;
    
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          if (!timeUpFired.current && isMyTurn && onTimeUp) {
            timeUpFired.current = true;
            onTimeUp();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isMyTurn, timeLimit, currentPlayer, onTimeUp]);
  
  return (
    <TimerContainer>
      <TimerDisplay $timeRemaining={timeRemaining}>
        {isMyTurn ? '⏱️ Din tid:' : `⏱️ ${currentPlayer}s tid:`} {timeRemaining}s
      </TimerDisplay>
    </TimerContainer>
  );
};

export default OnlineGameMode;

function formatTimestamp(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function formatGameEvent(event: any, players: OnlinePlayer[]): string {
  const playerName = players.find((p) => p.id === event.playerId)?.name ?? 'Okänd spelare';
  switch (event.type) {
    case 'player_left':
      return `${playerName} lämnade spelet.`;
    case 'player_disconnected':
      return `${playerName} tappade anslutningen.`;
    case 'game_started':
      return 'Spelet startade!';
    case 'dice_rolled':
      return `${playerName} kastade tärningarna.`;
    case 'score_selected':
      return `${playerName} valde kategori ${event.category} för ${event.value} poäng.`;
    case 'game_finished':
      return 'Spelet avslutades.';
    default:
      return event?.message ?? 'Händelse inträffade.';
  }
}